import React, { useMemo } from 'react'
import { ReportElement as GqlReportElement } from 'gqlTypes'
import ReportElementSettings from './ReportElementSettings'
import { ASTNode, DefsOptions, Dictionary, Options } from 'highcharts'
import { Box, Typography } from '@mui/material'
import Highcharts from 'highcharts'
import HighchartsPatternFill from 'highcharts/modules/pattern-fill'
import BarChartElement from 'components/BarChartElement'
import { BarChartMarginLeft, ChartColorOptions } from '.'
import useReportElementChart from 'hooks/useReportElementChart'
import getBarChartData, {
  getBarChartColors,
  getBarChartHeight,
  getCohortToUse,
  getSubgroupToUse,
} from 'util/getBarChartData'
import ReportElementLegend from './ReportElementLegend'
import ReportElementChartWithLegend from './ReportElementChartWithLegend'
import ReportElementFloatingLegend from './ReportElementFloatingLegend'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { ReportElementProps } from './ReportElement'

HighchartsPatternFill(Highcharts)

interface CustomPoint extends Highcharts.Point {
  custom?: {
    pointLabel: string
    axisLabel: string
    tooltipLabel: string
  }
}

const ReportElementToggledBarChart: React.FC<ReportElementProps> = ({
  reportId,
  element,
  sectionId,
  report,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    cohort,
    subgroup,
    pastResults,
    parsedData,
    parsedContent,
    dataToggles,
    data,
    pastResultNames,
    hidden,
    handleSetHidden,
    elementTitle,
  } = useReportElementChart(element, reportId)

  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    const cohortToUse: string = getCohortToUse(cohort || '', elementCohorts)
    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    const charts = useMemo(() => {
      const toggles = parsedContent.data_toggles
      let seriesData = getBarChartData({
        data,
        toggles,
        cohort: cohortToUse,
        subgroup: subgroupToUse,
        pastResults,
        parsedData,
      })
      const isPercent = parsedContent.data_type === 'percent'
      const { defs } = ChartColorOptions(seriesData.length)

      return (data?.categories || []).map((category: string, i: number) => {
        let catData = seriesData
          .map((d, i2) => {
            const chartColors = getBarChartColors(seriesData.length)
            let label = d.type === 'bar' && d.data ? d.data[i] : undefined
            let value = d.type === 'bar' && d.data ? d.data[i] : 0
            //Check for negative values
            if (
              typeof label === 'string' ||
              (typeof label === 'number' && label < 0) ||
              label === undefined
            ) {
              label = 'N/A'
              value = (data && data.y_min) || 0
            }
            return {
              name: d.name,
              type: 'bar',
              color: chartColors[i2],
              custom: {
                pointLabel: label,
                axisLabel: d.name,
                tooltipLabel: d.name,
              },
              y: value,
            }
          })
          .filter((d) => d.name != 'None')
        const options = {
          title: category,
          initialCategories: catData.map((d) => d.name),
          options: {
            title: undefined,
            colors: [],
            exporting: {
              buttons: {
                contextButton: {
                  enabled: false,
                },
              },
            },
            chart: {
              height: 0,
              type: 'bar',
              marginLeft: BarChartMarginLeft,
              width: print ? 1200 : null,
            },
            xAxis: {
              type: 'category',
              uniqueNames: false,
              labels: {
                style: {
                  color: print ? 'black' : undefined,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  width: `${BarChartMarginLeft - 20}px`,
                },
              },
            },
            yAxis: {
              min: data.y_min || 0,
              max: data.y_max || 100,
              tickInterval: data && isPercent ? 10 : data.y_interval,
              title: undefined,
              labels: {
                formatter: function (
                  this: Highcharts.AxisLabelsFormatterContextObject
                ): string {
                  return data && isPercent ? `${this.value}%` : `${this.value}`
                },
                style: {
                  color: print ? 'black' : undefined,
                },
              },
            },
            plotOptions: {
              series: {
                animation: !print,
              },
              bar: {
                pointWidth: 20,
                grouping: false,
                dataLabels: {
                  enabled: true,
                  formatter: function (
                    this: Highcharts.PointLabelObject
                  ): string {
                    const point: CustomPoint = this.point
                    if (point.custom?.pointLabel === 'N/A') {
                      return point.custom.pointLabel
                    }
                    return data && isPercent ? `${this.y}%` : `${this.y}`
                  },
                  style: {
                    fontSize: '14px',
                  },
                },
              },
            },
            tooltip: {
              formatter: function (
                this: Highcharts.TooltipFormatterContextObject
              ): string {
                const point: CustomPoint = this.point
                let value = data && isPercent ? this.y + '%' : this.y
                let label = point.custom?.tooltipLabel
                  ? point.custom?.tooltipLabel
                  : this.x
                if (point.custom?.pointLabel === 'N/A') {
                  value = point.custom.pointLabel
                }
                return `<span>${label}</span></br><span>${value}</span>`
              },
            },
            credits: {
              enabled: false,
            },
            defs: defs as DefsOptions | Dictionary<ASTNode> | undefined,
            legend: {
              enabled: false,
            },
            series: {
              data: catData.filter((d) =>
                pastResults
                  ? !hidden.includes(d.name)
                  : !hidden.includes(d.name) &&
                    !pastResultNames.includes(d.name)
              ),
            },
          },
        }
        options.options.chart.height = getBarChartHeight(
          'toggledBarChart',
          options.options
        )
        return options
      })
    }, [
      parsedData,
      parsedContent,
      cohortToUse,
      subgroupToUse,
      pastResults,
      hidden,
    ])

    const showHideOptions = charts[0].initialCategories
      .map((d: any) => ({ name: d }))
      .filter((item: { name: string }) =>
        pastResults ? true : !pastResultNames.includes(item.name)
      )
      .map((item: { name: string }) => ({
        key: item.name,
        title: item.name,
      }))

    if (print) {
      return (
        <Box sx={{ mb: 4, width: '100%', breakInside: 'avoid' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ breakInside: 'avoid' }}>
                <th style={{ textAlign: 'left' }}>
                  {elementTitle && (
                    <Box>
                      <Typography variant="h5">{elementTitle}</Typography>
                    </Box>
                  )}
                  <ReportElementLegend content={parsedContent} data={data} />
                  <Box mt={2}>
                    <ReportElementSettings
                      print={print}
                      reportId={reportId}
                      element={element}
                      sectionId={sectionId}
                      report={report}
                      noSubgroup={
                        dataToggles === 'Overall' ||
                        (data.segmentations || []).length === 0
                      }
                      noCohort={
                        dataToggles != 'Overall' ||
                        (data.cohorts || []).length === 0
                      }
                      noPastResults={
                        dataToggles != 'Overall' ||
                        (data['past_results'] || []).length === 0
                      }
                      showHideOptions={showHideOptions}
                      hidden={hidden}
                      onChangeHidden={handleSetHidden}
                      favorites={favorites}
                      onUpdateFavorites={onUpdateFavorites}
                    />
                  </Box>
                </th>
              </tr>
            </thead>
            <tbody>
              {charts.map(
                (c: { title: string; options: Options }, i: number) => (
                  <tr key={i} style={{ breakInside: 'avoid' }}>
                    <td>
                      <div>
                        <div>
                          <Box mt={2} mb={1}>
                            <Typography variant="h6" component="h6">
                              {c.title}
                            </Typography>
                          </Box>
                          <BarChartElement options={c.options} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
            <tfoot>
              <tr style={{ breakInside: 'avoid' }}>
                <td>
                  <ReportElementFloatingLegend
                    items={charts[0].options.series.data}
                    hidden={hidden}
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </Box>
      )
    }

    return (
      <Box sx={{ mb: 4, width: '100%', breakInside: 'avoid' }}>
        {elementTitle && (
          <Box px={print ? 0 : 4}>
            <Typography variant="h5">{elementTitle}</Typography>
          </Box>
        )}
        {charts.length > 0 && (
          <div>
            <Box mt={2}>
              <ReportElementSettings
                print={print}
                reportId={reportId}
                element={element}
                sectionId={sectionId}
                report={report}
                noSubgroup={
                  dataToggles === 'Overall' ||
                  (data.segmentations || []).length === 0
                }
                noCohort={
                  dataToggles != 'Overall' || (data.cohorts || []).length === 0
                }
                noPastResults={
                  dataToggles != 'Overall' ||
                  (data['past_results'] || []).length === 0
                }
                showHideOptions={showHideOptions}
                hidden={hidden}
                onChangeHidden={handleSetHidden}
                favorites={favorites}
                onUpdateFavorites={onUpdateFavorites}
              />
            </Box>
            <ReportElementLegend content={parsedContent} data={data} />
            <ReportElementChartWithLegend
              print={print}
              legend={
                <ReportElementFloatingLegend
                  items={charts[0].options.series.data}
                  hidden={hidden}
                />
              }
              chart={
                <Box>
                  {charts.map(
                    (c: { title: string; options: Options }, i: number) => (
                      <div key={i}>
                        <div>
                          <Box mt={2} mb={1}>
                            <Typography variant="h6" component="h6">
                              {c.title}
                            </Typography>
                          </Box>
                          <BarChartElement options={c.options} />
                        </div>
                      </div>
                    )
                  )}
                </Box>
              }
            />
          </div>
        )}
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementToggledBarChart
